import { useListAuditLog, useListCompanies } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Activity } from "lucide-react";
import InfoTip from "@/components/info-tip";

export default function Audit({ cohortSlug }: { cohortSlug?: string } = {}) {
  const { data: auditLog, isLoading } = useListAuditLog({ limit: 100 });
  const { data: companies } = useListCompanies();

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  return (
    <div className="space-y-6" data-testid="page-audit">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Audit Trail</h1>
          <InfoTip content="A complete record of every action taken in the system — who did what, when, and to which company. Every create, update, and approval is logged here for transparency and accountability. This is your compliance and provenance layer." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">Full provenance tracking across all operations</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : auditLog && auditLog.length > 0 ? (
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
          <div className="space-y-0">
            {auditLog.map((entry) => (
              <div key={entry.id} className="relative pl-10 py-3" data-testid={`audit-entry-${entry.id}`}>
                <div className="absolute left-3 top-4 w-2.5 h-2.5 rounded-full bg-muted-foreground/30 border-2 border-background" />
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm">{entry.details || `${entry.entity} ${entry.action}`}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {entry.companyId && (
                        <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${entry.companyId}` : `/companies/${entry.companyId}`}>
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-accent">
                            {companyMap.get(entry.companyId) ?? `#${entry.companyId}`}
                          </Badge>
                        </Link>
                      )}
                      <Badge variant="secondary" className="text-xs">{entry.entity}</Badge>
                      <Badge variant="outline" className="text-xs capitalize">{entry.action}</Badge>
                      {entry.performedBy && <span className="text-xs text-muted-foreground">by {entry.performedBy}</span>}
                      {entry.source && <Badge variant="outline" className="text-xs">{entry.source}</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-4">{new Date(entry.createdAt).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No audit entries yet</p>
            <p className="text-sm text-muted-foreground mt-1">Actions will be tracked here with full provenance</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
